/**
 * Phase 124 — Operator Activation Hub at /arenax/host
 *
 * The growth funnel surface.  Any user can land here and progressively
 * activate operator capabilities — Academy, Venue/Turf, Tournament
 * Organizer.  Already-activated capabilities show a "Manage" affordance
 * that links into the existing Out-Play workspace (federated context).
 *
 * Designed under the CEO doctrine:
 *   "Turn your ground into a living sports venue."
 *   Not admin software — sports presence activation.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import ArenaxLayout from './ArenaxLayout';
import { ArenaxWordmark } from './ArenaxWordmark';
import {
  Building2, MapPin, Trophy, ArrowRight, CheckCircle2, ExternalLink, Loader2,
} from 'lucide-react';

const CARDS = [
  {
    key: 'academy',
    icon: Building2,
    tag: 'Academy',
    title: 'Bring your academy onto ArenaX.',
    sub: 'Publish your academy, run leagues, host games, build a recurring community.',
    cta: 'Register Academy',
    manage_cta: 'Manage academy',
    manage_href: '/dashboard',
    activate_href: '/arenax/host/academy',
    accent: '#10b981',
  },
  {
    key: 'venue',
    icon: MapPin,
    tag: 'Venue / Turf',
    title: 'List your turf, accept bookings.',
    sub: 'Publish slots, surface your venue in the city pulse, monetise underused time.',
    cta: 'List Venue',
    manage_cta: 'Manage venues',
    manage_href: '/dashboard',
    activate_href: '/arenax/host/venue',
    accent: '#0891b2',
  },
  {
    key: 'organizer',
    icon: Trophy,
    tag: 'Tournament Organizer',
    title: 'Run city tournaments, recurring leagues.',
    sub: 'Build the rivalries that turn into mythology. Defend champions across seasons.',
    cta: 'Become Organizer',
    manage_cta: 'Manage tournaments',
    manage_href: '/arenax/tournaments',
    activate_href: '/arenax/host/organizer',
    accent: '#f59e0b',
  },
];

export default function ArenaxOperatorHost() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) {
      // Public visitor — still allow viewing the hub but no activation state.
      setLoading(false);
      return;
    }
    api.get('/arenax/me/operator-status')
      .then(r => setStatus(r.data))
      .catch(e => setError(e?.response?.data?.detail || 'Unable to load activation state.'))
      .finally(() => setLoading(false));
  }, [user]);

  const flagFor = (key) => {
    if (!status) return false;
    if (key === 'academy') return status.has_academy;
    if (key === 'venue') return status.has_venue;
    if (key === 'organizer') return status.has_organizer;
    return false;
  };

  return (
    <ArenaxLayout>
      <section className="max-w-5xl mx-auto px-5 py-12 arenax-themed" data-testid="arenax-host-page">
        <div className="text-center mb-12 space-y-4">
          <div className="inline-flex items-center justify-center mb-2">
            <ArenaxWordmark size="lg" />
          </div>
          <h1
            className="text-3xl sm:text-4xl font-light tracking-tight"
            style={{ color: `hsl(var(--ax-text))`, fontFamily: 'Georgia, "Times New Roman", serif' }}
            data-testid="arenax-host-title"
          >
            Turn your ground into a living sports venue.
          </h1>
          <p className="text-sm sm:text-base max-w-2xl mx-auto leading-relaxed"
             style={{ color: `hsl(var(--ax-text-muted))` }}>
            Accept bookings. Host leagues. Build recurring communities.<br />
            One identity. Progressive capabilities. No second account.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin" style={{ color: `hsl(var(--ax-accent-cyan))` }} />
          </div>
        ) : (
          <>
            {error && (
              <div className="text-center text-sm italic mb-6"
                   style={{ color: 'hsl(0 70% 55%)' }}
                   data-testid="arenax-host-error">{error}</div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="arenax-host-cards">
              {CARDS.map(card => {
                const active = flagFor(card.key);
                const Icon = card.icon;
                return (
                  <div
                    key={card.key}
                    className="arenax-card p-6 flex flex-col gap-4"
                    style={{ borderRadius: '12px' }}
                    data-testid={`arenax-host-card-${card.key}`}
                  >
                    <div className="flex items-start justify-between">
                      <div
                        className="h-10 w-10 rounded-lg flex items-center justify-center"
                        style={{ background: `${card.accent}1a`, color: card.accent }}
                      >
                        <Icon className="h-5 w-5" />
                      </div>
                      {active && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full"
                          style={{
                            background: 'rgba(16,185,129,0.10)',
                            color: '#047857',
                            border: '1px solid rgba(16,185,129,0.25)',
                          }}
                          data-testid={`arenax-host-card-${card.key}-active`}
                        >
                          <CheckCircle2 className="h-3 w-3" /> Activated
                        </span>
                      )}
                    </div>
                    <div className="space-y-2">
                      <div className="text-[10px] uppercase tracking-[0.28em]"
                           style={{ color: `hsl(var(--ax-text-faint))` }}>
                        {card.tag}
                      </div>
                      <h3 className="text-base font-bold leading-snug"
                          style={{ color: `hsl(var(--ax-text))` }}>
                        {card.title}
                      </h3>
                      <p className="text-xs leading-relaxed"
                         style={{ color: `hsl(var(--ax-text-muted))` }}>
                        {card.sub}
                      </p>
                    </div>
                    {active ? (
                      <a
                        href={card.manage_href}
                        className="mt-auto inline-flex items-center gap-1.5 text-xs font-semibold py-2.5 px-3 rounded-lg border transition-colors"
                        style={{
                          color: `hsl(var(--ax-text))`,
                          borderColor: 'var(--ax-border-quiet)',
                          background: 'var(--ax-bg-soft)',
                        }}
                        data-testid={`arenax-host-card-${card.key}-manage`}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        {card.manage_cta}
                      </a>
                    ) : (
                      <button
                        onClick={() => navigate(card.activate_href)}
                        className="mt-auto inline-flex items-center justify-center gap-1.5 text-xs font-bold py-2.5 rounded-lg transition-all hover:-translate-y-0.5"
                        style={{
                          background: 'var(--ax-primary-gradient)',
                          color: `hsl(var(--ax-button-text))`,
                          boxShadow: 'var(--ax-shadow-glow)',
                        }}
                        data-testid={`arenax-host-card-${card.key}-cta`}
                      >
                        {card.cta} <ArrowRight className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            <div
              className="mt-10 text-center text-[11px] italic leading-relaxed"
              style={{ color: `hsl(var(--ax-text-faint))` }}
            >
              One ArenaX identity. Activate more capabilities anytime —
              your role inside Out-Play stays untouched.
            </div>
          </>
        )}
      </section>
    </ArenaxLayout>
  );
}
